import { z } from 'zod';
import {
  CharacterActivityKindSchema,
  CharacterActivityTargetSchema,
} from './characterPresence';

// ============================================
// 角色运行时状态
// ============================================

// ---------- 基础枚举 ----------

/** 角色当前位置的场景类型 */
export const CharacterRuntimeLocationKindSchema = z.enum([
  'unknown',
  'world',
  'city',
  'doorstep',
  'building',
  'traveling',
  'party',
  'dead',
]);
export type CharacterRuntimeLocationKind = z.infer<typeof CharacterRuntimeLocationKindSchema>;

/** 角色当前活动所处阶段 */
export const CharacterRuntimeActivityPhaseSchema = z.enum(['idle', 'traveling', 'executing', 'blocked']);
export type CharacterRuntimeActivityPhase = z.infer<typeof CharacterRuntimeActivityPhaseSchema>;

/** 角色当前活动的来源 */
export const CharacterRuntimeActivitySourceSchema = z.enum(['commitment', 'ambient', 'fallback', 'manual', 'system']);
export type CharacterRuntimeActivitySource = z.infer<typeof CharacterRuntimeActivitySourceSchema>;

/** 角色所在坐标层级：世界地图或城市内部 */
export const CharacterRuntimePositionLayerSchema = z.enum(['world', 'city']);
export type CharacterRuntimePositionLayer = z.infer<typeof CharacterRuntimePositionLayerSchema>;

// ---------- 位置与移动 ----------

/** 角色在地图上的二维坐标 */
export const CharacterRuntimePositionSchema = z.object({
  layer: CharacterRuntimePositionLayerSchema.default('world'),
  x: z.number().default(0),
  y: z.number().default(0),
});
export type CharacterRuntimePosition = z.infer<typeof CharacterRuntimePositionSchema>;

/** 角色当前主活动 */
export const CharacterRuntimeActivitySchema = z.object({
  id: z.string().min(1),
  kind: CharacterActivityKindSchema,
  phase: CharacterRuntimeActivityPhaseSchema.default('idle'),
  source: CharacterRuntimeActivitySourceSchema.default('fallback'),
  target: CharacterActivityTargetSchema.optional(),
  reason: z.string().default(''),
  priority: z.number().int().default(0),
  lockedUntilArrive: z.boolean().default(true),
  startedAt: z.number().optional(),
});
export type CharacterRuntimeActivity = z.infer<typeof CharacterRuntimeActivitySchema>;

/** 角色移动状态，记录目标与速度 */
export const CharacterRuntimeMovementSchema = z.object({
  targetPosition: CharacterRuntimePositionSchema,
  targetZoneId: z.string().default(''),
  targetBuildingId: z.string().default(''),
  travelMode: z.enum(['city_road', 'world_route']).optional(),
  metric: z.enum(['city_unit', 'km']).optional(),
  startedAt: z.number().optional(),
  lastResolvedAt: z.number().optional(),
  speedPerMinute: z.number().nonnegative().default(0),
  pathPoints: z.array(CharacterRuntimePositionSchema).optional(),
  pathDistance: z.number().nonnegative().optional(),
  pathDistanceKm: z.number().nonnegative().optional(),
  progressDistance: z.number().nonnegative().optional(),
  estimatedTravelMinutes: z.number().nonnegative().optional(),
  elapsedTravelMinutes: z.number().nonnegative().optional(),
  startedAtGameMinutes: z.number().nonnegative().optional(),
});
export type CharacterRuntimeMovement = z.infer<typeof CharacterRuntimeMovementSchema>;

const DEFAULT_RUNTIME_ACTIVITY: CharacterRuntimeActivity = {
  id: 'runtime_idle',
  kind: 'idle',
  phase: 'idle',
  source: 'fallback',
  reason: '',
  priority: 0,
  lockedUntilArrive: true,
};

export function isCharacterRuntimeTraveling(
  runtimeState:
    | {
        locationKind: CharacterRuntimeLocationKind;
        currentActivity?: CharacterRuntimeActivity;
      }
    | null
    | undefined,
): boolean {
  if (!runtimeState) return false;
  return runtimeState.locationKind === 'traveling' || runtimeState.currentActivity?.phase === 'traveling';
}

// ---------- 组合状态 ----------

/** 角色运行时完整状态，包含位置、活动、移动等信息 */
export const CharacterRuntimeStateSchema = z.object({
  currentZoneId: z.string().default(''),
  currentBuildingId: z.string().default(''),
  locationKind: CharacterRuntimeLocationKindSchema.default('unknown'),
  position: CharacterRuntimePositionSchema.default({
    layer: 'world',
    x: 0,
    y: 0,
  }),
  currentActivity: CharacterRuntimeActivitySchema.default(DEFAULT_RUNTIME_ACTIVITY),
  movement: CharacterRuntimeMovementSchema.optional(),
  lastResolvedAt: z.number().optional(),
});

export type CharacterRuntimeStateInput = z.input<typeof CharacterRuntimeStateSchema>;
export type CharacterRuntimeState = z.infer<typeof CharacterRuntimeStateSchema>;
